import { useEffect, useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { dv } from '../../i18n/dv.ts';
import { yaumiyyaApi } from '../yaumiyya/api.ts';
import { displayName } from '../../lib/utils.ts';
import { Dropdown } from '../../components/Dropdown.tsx';
import type { MeetingType, Body } from './api.ts';

export interface MeetingFormValues {
  title: string;
  scheduledAt: string; // ISO 8601
  location: string;
  meetingType: MeetingType;
  body: Body;
  // Who the signed agenda is emailed to. Required (≥1) in create mode.
  recipientIds: string[];
}

interface MeetingFormProps {
  mode: 'create' | 'edit';
  initial: MeetingFormValues;
  // Create mode only: bodies the user may schedule for. A selector is shown
  // when there's more than one; otherwise the single body is sent implicitly.
  schedulableBodies?: Body[];
  pending: boolean;
  errorMessage: string | null;
  onSubmit: (values: MeetingFormValues) => void;
  onCancel?: () => void;
}

// Meetings are always scheduled in Maldives time (MVT, fixed UTC+5, no DST),
// regardless of the editor's device timezone — so the datetime-local input shows
// and is interpreted as MVT, not the browser's local zone. (A non-MVT editor
// would otherwise enter/see the wrong wall-clock.)
const MVT_OFFSET_MS = 5 * 60 * 60 * 1000;
const pad = (n: number): string => String(n).padStart(2, '0');

// Stored UTC ISO → the MVT wall-clock string the datetime-local input expects.
// Exported — also used by MeetingDetail's reschedule dialog.
export function isoToMvtInput(iso: string): string {
  if (!iso) return '';
  const d = new Date(new Date(iso).getTime() + MVT_OFFSET_MS);
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
}

// The datetime-local value is MVT wall-clock → convert to a UTC ISO instant by
// treating the entered time as MVT and subtracting the +5h offset.
export function mvtInputToIso(when: string): string {
  const [datePart, timePart] = when.split('T');
  const [y, mo, da] = datePart!.split('-').map(Number);
  const [h, mi] = timePart!.split(':').map(Number);
  return new Date(Date.UTC(y!, mo! - 1, da!, h!, mi!) - MVT_OFFSET_MS).toISOString();
}

export function MeetingForm({ mode, initial, schedulableBodies = [], pending, errorMessage, onSubmit, onCancel }: MeetingFormProps): JSX.Element {
  const [title, setTitle] = useState(initial.title);
  const [when, setWhen] = useState(isoToMvtInput(initial.scheduledAt));
  const [location, setLocation] = useState(initial.location);
  const [meetingType, setMeetingType] = useState<MeetingType>(initial.meetingType);
  const [createBody, setCreateBody] = useState<Body>(initial.body);
  // Agenda-email recipients (create mode). Loaded per the selected body; the
  // body-appropriate people are all selected by default. Editing recipients on
  // an existing meeting happens in a separate panel, so the picker is create-only.
  const [recipientIds, setRecipientIds] = useState<string[]>(initial.recipientIds);
  const recipientsBody = mode === 'create' ? createBody : initial.body;
  const { data: members } = useQuery({
    queryKey: ['council-members', recipientsBody],
    queryFn: () => yaumiyyaApi.councilMembers(recipientsBody),
    enabled: mode === 'create',
  });
  // When the members for the selected body arrive (or the body changes), default
  // to selecting everyone returned — they are exactly the body-appropriate set.
  useEffect(() => {
    if (mode === 'create' && members) setRecipientIds(members.map((m) => m.id));
  }, [mode, members]);

  const noRecipients = mode === 'create' && recipientIds.length === 0;

  function toggleRecipient(id: string): void {
    setRecipientIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  }

  function submit(e: FormEvent): void {
    e.preventDefault();
    onSubmit({
      title,
      scheduledAt: mvtInputToIso(when),
      location,
      meetingType,
      body: mode === 'create' ? createBody : initial.body,
      recipientIds,
    });
  }

  return (
    <form className="space-y-3" onSubmit={submit}>
      <div className="space-y-1">
        <label className="field-label">{dv.meetings.meetingTitle}</label>
        <input required value={title} onChange={(e) => setTitle(e.target.value)} className="input" />
      </div>
      <div className="flex flex-wrap gap-3">
        <div className="space-y-1">
          <label className="field-label">{dv.meetings.dateTime}</label>
          <input
            required
            type="datetime-local"
            dir="ltr"
            value={when}
            onChange={(e) => setWhen(e.target.value)}
            className="input"
          />
        </div>
        <div className="flex-1 space-y-1">
          <label className="field-label">{dv.meetings.location}</label>
          <input required value={location} onChange={(e) => setLocation(e.target.value)} className="input" />
        </div>
        <div className="space-y-1">
          <label className="field-label">{dv.meetings.type}</label>
          <Dropdown
            value={meetingType}
            options={(['GENERAL', 'EMERGENCY'] as const).map((t) => ({ value: t, label: dv.meetings.typeLabel[t] ?? t }))}
            onChange={(v) => setMeetingType(v as MeetingType)}
          />
        </div>
        {mode === 'create' && schedulableBodies.length > 1 && (
          <div className="space-y-1">
            <label className="field-label">{dv.body.label}</label>
            <Dropdown
              value={createBody}
              options={schedulableBodies.map((b) => ({ value: b, label: dv.body[b] ?? b }))}
              onChange={(v) => setCreateBody(v as Body)}
            />
          </div>
        )}
      </div>
      {mode === 'create' && (
        <div className="space-y-1.5">
          <label className="field-label">{dv.meetings.recipients}</label>
          <p className="text-xs text-muted-foreground">{dv.meetings.recipientsHint}</p>
          <div className="flex flex-col gap-2 rounded-2xl border border-[hsl(var(--border))] p-3">
            {!members ? (
              <span className="text-sm text-muted-foreground">{dv.common.loading}</span>
            ) : members.length === 0 ? (
              <span className="text-sm text-muted-foreground">{dv.common.none}</span>
            ) : members.map((m) => (
              <label key={m.id} className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={recipientIds.includes(m.id)} onChange={() => toggleRecipient(m.id)} />
                <span>{displayName(m)}</span>
                <span className="text-xs text-muted-foreground">{m.designationDv ?? dv.roles[m.role] ?? m.role}</span>
              </label>
            ))}
          </div>
          {noRecipients && <p className="text-sm text-destructive">{dv.meetings.recipientsRequired}</p>}
        </div>
      )}
      {errorMessage && <p className="text-sm text-destructive">{errorMessage}</p>}
      <div className="flex items-center gap-2">
        <button type="submit" disabled={pending || noRecipients} className="btn-primary">{dv.common.save}</button>
        {onCancel && (
          <button type="button" onClick={onCancel} className="btn-ghost">{dv.common.cancel}</button>
        )}
      </div>
    </form>
  );
}
